import React, { useState } from 'react';
import { Accordion, Box, Paper, Stack, Text, Title } from '@mantine/core';

import { FuturePricesQueryPanel } from './FuturePricesQueryPanel';
import { useAuth } from '../../../context/AuthProvider';
import { checkUserRole } from '../../../utility/userRoles';
import { UserRole } from '../../../types/users';

const accordionClassNames = {
    control: 'pl-0 pr-0 rounded-lg',
    item: 'border-none',
};

export type FuturePlannedPricesCollapsibleProps = {
    hasFuturePrice?: boolean | null;
    productId?: string | null;
    /** Match drawer RequestProductInfo white card wrapper */
    withDrawerSurfaces?: boolean;
};

export function FuturePlannedPricesCollapsible({
    hasFuturePrice,
    productId,
    withDrawerSurfaces = false,
}: FuturePlannedPricesCollapsibleProps) {
    const [accordionValue, setAccordionValue] = useState<string | null>(null);
    const { authUser } = useAuth();
    const canSeeCostPrices = !checkUserRole(authUser, [UserRole.STORE_MANAGER]);

    if (!hasFuturePrice || !productId) {
        return null;
    }

    const accordion = (
        <Accordion
            classNames={accordionClassNames}
            value={accordionValue}
            onChange={setAccordionValue}
        >
            <Accordion.Item value="future-planned-prices">
                <Accordion.Control>
                    <Stack gap={2} className="items-start text-left">
                        <Title order={4}>Future planned prices</Title>
                        <Text size="sm" c="dimmed">
                            {canSeeCostPrices
                                ? 'Future-dated selling and cost prices scheduled for this product.'
                                : 'Future-dated selling prices scheduled for this product.'}
                        </Text>
                    </Stack>
                </Accordion.Control>
                <Accordion.Panel className="pt-1">
                    <FuturePricesQueryPanel
                        productId={productId}
                        skip={accordionValue !== 'future-planned-prices'}
                        embedded
                    />
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    );

    if (withDrawerSurfaces) {
        return (
            <Paper radius="lg" shadow="sm" className="mb-5 bg-white px-6 py-3">
                {accordion}
            </Paper>
        );
    }

    return <Box className="min-w-0 w-full">{accordion}</Box>;
}
